import type { ShopContentQuery_ShopContent } from '~/@types/nestedGraphqlTypes';
import type { MenuItem, PageByUrlQuery, WebsiteQuery } from '~/graphql/generated';
import type { LocationQueryValue } from '#vue-router';

type pageStatus = 'pending' | 'success' | 'error';

// https://pinia.vuejs.org/core-concepts/#setup-stores
export const useAppStore = defineStore('woom-store-app', () => {
    const website = ref<WebsiteQuery_Website | null>(null);
    const shopInfo = ref<ShopContentQuery_ShopContent>([]);
    const pageCache = ref<PageByUrlQuery_PageByUrl | null>(null);
    const variantData = ref<VariantQuery_ShopVariant[]>([]);
    const pageStatus = ref<pageStatus>('pending');
    const pageError = ref<{ statusCode: number; message: string } | undefined>(undefined);

    function saveExtendedPayloadData<T>(data: ExtendedApolloQueryResult<T> | undefined) {
        if (!data) {
            return;
        }
        // add new entries, or replace existing ones. Never clear or replace the entire array
        if (data.variantData?.data) {
            const reducedVariantData = (data.variantData.data || []).reduce((previousValue, currentValue) => {
                const currentValueInner = currentValue.shopVariant;

                const filtered = previousValue.filter(
                    (x) => x?.product.id !== currentValueInner?.product.id || x?.variant.id !== currentValueInner?.variant.id,
                );

                return [...filtered, currentValueInner];
            }, variantData.value);

            variantData.value = reducedVariantData;
        }

        // add new entries, or replace existing ones. Never clear or replace the entire array
        if (data.shopData?.data) {
            const reducedShopData = data.shopData.data.shopContent.reduce((previousValue, currentValue) => {
                const filtered = previousValue.filter((x) => x.id !== currentValue.id);

                return [...filtered, currentValue];
            }, shopInfo.value);

            shopInfo.value = reducedShopData;
        }
    }
    async function fetchWebsiteConfig(locale: string, previewQueryParams: Record<string, LocationQueryValue | LocationQueryValue[]>) {
        shopInfo.value = [];
        pageCache.value = null;
        const fetchOptions = useGenericFetchOptions();

        const websiteQueryResult = await $fetch('/api/website', {
            params: {
                locale,
                codename: 'website',
                ...previewQueryParams,
            },
            ...fetchOptions,
        });

        const websiteData = websiteQueryResult?.data?.website;
        if (!websiteData) return;

        // @ts-ignore TODO: better API types should fix this
        website.value = websiteData;
        saveExtendedPayloadData(websiteQueryResult as ExtendedApolloQueryResult<WebsiteQuery>);
    }

    function getMenuItemById(id: string) {
        return website.value?.navbar?.references?.find((m) => m.id === id) as MenuItem | undefined;
    }

    function getReference<T extends { id: string }>(id: string, type = 'references') {
        const pageCacheRefs = pageCache.value?.refs;
        if (pageCacheRefs) return getItemById<T>(id, (pageCacheRefs as any)?.[type]);
    }

    function hasPageCache(url: string, locale: string) {
        const cacheUrl = pageCache.value?.url?.url || null;
        const cacheLocale = pageCache.value?.url?.locale || null;
        return cacheUrl === url && cacheLocale === locale;
    }

    async function loadPageByUrl({ locale, url }: { locale: string; url: string }) {
        // fix for urls that end with a slash (urls in the database are without trailing slash)
        if (url.length > 1 && url.substring(url.length - 1) === '/') {
            url = url.substring(0, url.length - 1);
        }

        pageStatus.value = 'pending';
        pageError.value = undefined;

        // reset pdp availibility for mobile nav bar opacity
        setPdpAvailability('');

        try {
            const { previewEnabed, queryParams } = usePreview();

            if (!hasPageCache(url, locale) || previewEnabed.value) {
                const fetchOptions = useGenericFetchOptions();
                const data = await $fetch<ExtendedApolloQueryResult<PageByUrlQuery>>('/api/page-by-url', {
                    params: {
                        locale,
                        url,
                        full: true,
                        ...queryParams.value,
                    },
                    ...fetchOptions,
                });

                const pd: PageByUrlQuery_PageByUrl = data.data.pageByUrl || null;
                pageCache.value = pd;
                if (!pd) {
                    throw createError({
                        statusCode: 404,
                        statusMessage: 'Page not found',
                    });
                }
                saveExtendedPayloadData(data);
            }
            pageStatus.value = 'success';
            return pageCache.value;
        } catch (err: any) {
            pageStatus.value = 'error';
            pageError.value = {
                statusCode: err.statusCode || 500,
                message: err.message || 'An unexpected error occurred',
            };
        }
    }

    // computed
    const footer = computed(() => website.value?.footer);
    const imperial = computed(() => units.value.imperial);
    const menuItems = computed(() => {
        const navbar = website.value?.navbar;
        return navbar?.items?.map((itemId) => navbar?.references?.find(({ id }) => id === itemId) as MenuItem | undefined)?.filter(isDefined) ?? [];
    });
    const metric = computed(() => units.value.metric);
    const defaultSeo = computed(
        () =>
            website.value?.seo || {
                image: null,
                title: 'woom bikes',
                description: '',
            },
    );
    const socialLinks = computed(() => {
        const socialInfo = website.value?.social;
        if (!socialInfo) return [];

        return Object.keys(socialInfo)
            .filter((platform) => platform !== '__typename')
            .map((platform) => {
                return {
                    key: platform,
                    url: (socialInfo as Record<string, any>)[platform],
                    icon: `woom-icon-${platform}`,
                };
            })
            .filter((p) => p.url);
    });
    const units = computed(() => {
        const units = website.value?.shop?.units;
        if (!units)
            return {
                imperial: true,
                metric: true,
            };

        return {
            metric: units.includes('metric'),
            imperial: units.includes('imperial'),
        };
    });
    const websiteUpsell = computed(() => website.value?.shop?.upsell || []);
    const schemaOrganisation = computed(() => website.value?.organisation);
    const searchConfiguration = computed(() => website.value?.search);

    // refs only used outside of store
    /**
     * only used in server side context
     */
    const clientIP = ref('');
    const hasH1 = ref(false);

    /**
     * logic used for mobile menu pdp
     */

    const _pdpAvailability = ref<'available' | string>('');
    function setPdpAvailability(availability: string) {
        _pdpAvailability.value = availability;
    }
    const showAddToCart = computed(() => _pdpAvailability.value === 'available');
    const _mobileMenuOpen = ref<boolean>(false);
    function setMobileMenuOpen(isOpen: boolean) {
        _mobileMenuOpen.value = isOpen;
    }
    const mobileMenuOpen = computed(() => _mobileMenuOpen.value);

    return {
        clientIP,
        defaultSeo,
        footer,
        hasH1,
        imperial,
        menuItems,
        metric,
        mobileMenuOpen,
        pageCache,
        shopInfo,
        socialLinks,
        variantData,
        website,
        websiteUpsell,
        pageStatus,
        pageError,
        schemaOrganisation,
        searchConfiguration,
        showAddToCart,
        fetchWebsiteConfig,
        getMenuItemById,
        getReference,
        loadPageByUrl,
        setMobileMenuOpen,
        setPdpAvailability,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAppStore, import.meta.hot));
}
